import type {FullCourse} from "@repo/sanity-utils/query-types";
import {getKeyedCopyText} from "@repo/utils/keyedCopy";
import {BEAST_ACADEMY_MODALITY_SLUG} from "./const";

export type InPersonBillingType = "trimester" | "once";

/** learning-format `coursePageSection` keyed copy for modality detail headers. */
export const WHY_CHOOSE_HEADER = {
	key: "why-choose-header",
	fallback: "Why choose {{modality.title}}:",
} as const;
export const BEST_FOR_HEADER = {
	key: "best-for-header",
	fallback: "Best for students who:",
} as const;
export const MODALITY_TITLE_PLACEHOLDER = "{{modality.title}}";

export const WHY_CHOOSE_KEY = WHY_CHOOSE_HEADER.key;
export const FALLBACK_WHY_CHOOSE_TITLE = WHY_CHOOSE_HEADER.fallback;
export const BEST_FOR_KEY = BEST_FOR_HEADER.key;
export const FALLBACK_BEST_FOR_TITLE = BEST_FOR_HEADER.fallback;

/**
 * Resolve in-person billing, defaulting to trimester (current AY behavior).
 */
export function resolveInPersonBillingType(
	value: InPersonBillingType | null | undefined
): InPersonBillingType {
	return value === "once" ? "once" : "trimester";
}

/** Format Details Billing quick-fact overrides for once-billed courses. */
const BILLING_QUICK_FACT_TITLE = "billing";
const BILLING_VALUE_ONCE_KEY = "billing-value-once";
const FALLBACK_BILLING_VALUE_ONCE = "Per course";
const BILLING_DESCRIPTION_ONCE_KEY = "billing-description-once";
const FALLBACK_BILLING_DESCRIPTION_ONCE = "Billed once at enrollment.";

type Modality = NonNullable<
	NonNullable<FullCourse>["modalityOfferings"]
>[number]["modality"];

export type ModalityQuickFact = NonNullable<
	NonNullable<Modality>["quickFacts"]
>[number];

function isBillingQuickFact(fact: ModalityQuickFact): boolean {
	return fact.title?.trim().toLowerCase() === BILLING_QUICK_FACT_TITLE;
}

function isInPersonModality(modality: Modality): boolean {
	return (
		modality?.slug?.current === "in-person" ||
		modality?.price?.displayMode === "campusLocation"
	);
}

/**
 * Whether Format Details Billing should use once-at-enrollment copy instead of
 * the shared modality Sanity quick fact (Per half / per trimester).
 */
function shouldUseOnceBillingQuickFact(
	modality: Modality,
	sequenceType: NonNullable<FullCourse>["sequenceType"],
	inPersonBillingType: InPersonBillingType | null | undefined
): boolean {
	const slug = modality?.slug?.current;
	if (!slug || slug === BEAST_ACADEMY_MODALITY_SLUG) {
		return false;
	}
	// Manual price modalities (e.g. Beast Academy subscription) keep Sanity copy.
	if (modality?.price?.displayMode === "manual") {
		return false;
	}
	if (isInPersonModality(modality)) {
		return resolveInPersonBillingType(inPersonBillingType) === "once";
	}
	return sequenceType !== "sequential";
}

/**
 * Override a Billing quick fact for once-billed / non-sequential courses.
 * Sequential online and in-person trimester keep shared modality Sanity copy.
 */
export function resolveModalityQuickFact(
	fact: ModalityQuickFact,
	modality: Modality,
	sequenceType: NonNullable<FullCourse>["sequenceType"],
	inPersonBillingType: InPersonBillingType | null | undefined = "trimester",
	keyedCopy: {key: string; text: string}[] = []
): ModalityQuickFact {
	if (
		!isBillingQuickFact(fact) ||
		!shouldUseOnceBillingQuickFact(modality, sequenceType, inPersonBillingType)
	) {
		return fact;
	}

	return {
		...fact,
		value:
			getKeyedCopyText(keyedCopy, BILLING_VALUE_ONCE_KEY) ||
			FALLBACK_BILLING_VALUE_ONCE,
		description:
			getKeyedCopyText(keyedCopy, BILLING_DESCRIPTION_ONCE_KEY) ||
			FALLBACK_BILLING_DESCRIPTION_ONCE,
	};
}

/**
 * Resolve modality Format Details quick facts for the current course.
 */
export function resolveModalityQuickFacts(
	facts: Modality["quickFacts"],
	modality: Modality,
	sequenceType: NonNullable<FullCourse>["sequenceType"],
	inPersonBillingType: InPersonBillingType | null | undefined = "trimester",
	keyedCopy: {key: string; text: string}[] = []
): ModalityQuickFact[] {
	return (facts ?? []).map((fact) =>
		resolveModalityQuickFact(
			fact,
			modality,
			sequenceType,
			inPersonBillingType,
			keyedCopy
		)
	);
}

/**
 * Resolve a modality offering id from its slug (e.g. query `?modality=video-based`).
 */
export function getModalityIdBySlug(
	course: FullCourse | null | undefined,
	modalitySlug: string | null | undefined
): string | null {
	if (!modalitySlug) return null;
	const offering = course?.modalityOfferings?.find(
		(modalityOffering) =>
			modalityOffering.modality?.slug?.current === modalitySlug
	);
	return offering?.modality?._id ?? null;
}
